import { createAnnotationLayer } from '../annotations';
import {
  WebGLRenderer,
  Scene,
  PerspectiveCamera,
  Mesh,
  PlaneGeometry,
  OrthographicCamera,
  type Camera,
  type ShaderMaterial,
  type WebGLRenderTarget,
} from 'three';
import type { StyleParams, QualitySettings, PostPipeline, NightFrame } from '../types';
import { createGBuffer, createPaintRT } from './renderTargets';
import {
  createFinalPassMaterial,
  updateFinalPassQuality,
  updateFinalPassDaylight,
  updateFinalPassPools,
} from './finalPass';
import { createKuwaharaMaterial } from './kuwaharaPass';
import { createGlowLayer } from '../night/glowLayer';
import { NIGHT, nearestLights } from '../night/nightLights';
import { PainterHandoff } from './painterHandoff';

export interface CompileStage {
  readonly target: WebGLRenderTarget | null;
  readonly scene: Scene;
  readonly camera: Camera;
}

/*
 * three keys a program on the render target bound when it is built (colour space, tone mapping,
 * draw buffers), so each scene compiles against the target it renders into: the world into the
 * G-buffer, the Kuwahara pass into the paint target at every tier, so that a tier switch never
 * compiles on its first frame, and the final pass onto the canvas.
 */
export function compileStages(
  world: { readonly scene: Scene; readonly camera: Camera },
  gbuffer: WebGLRenderTarget,
  paint: { readonly scene: Scene; readonly target: WebGLRenderTarget },
  finalScene: Scene,
  postCamera: Camera,
  glowScene?: Scene,
  handoffScene?: Scene
): readonly CompileStage[] {
  return [
    { target: gbuffer, scene: world.scene, camera: world.camera },
    { target: paint.target, scene: paint.scene, camera: postCamera },
    { target: null, scene: finalScene, camera: postCamera },
    /* The glows are drawn onto the canvas with the world's camera, after dark only; compiled here
       so that dusk does not stop a frame to build their program. */
    ...(glowScene ? [{ target: null, scene: glowScene, camera: world.camera }] : []),
    ...(handoffScene ? [{ target: null, scene: handoffScene, camera: postCamera }] : []),
  ];
}

/* compile() runs synchronously inside compileAsync, so each stage only needs its target bound
   while it starts; the driver then links all of them in parallel. */
async function compileProgramStages(
  renderer: WebGLRenderer,
  stages: readonly CompileStage[]
): Promise<void> {
  const parallel = renderer.extensions.has('KHR_parallel_shader_compile');
  const previous = renderer.getRenderTarget();
  const pending: Promise<unknown>[] = [];
  try {
    for (const stage of stages) {
      renderer.setRenderTarget(stage.target);
      if (parallel) pending.push(renderer.compileAsync(stage.scene, stage.camera));
      else renderer.compile(stage.scene, stage.camera);
    }
  } finally {
    renderer.setRenderTarget(previous);
  }
  await Promise.all(pending);
}

export function createPostPipeline(
  renderer: WebGLRenderer,
  style: StyleParams,
  quality: QualitySettings,
  glowMaterial?: ShaderMaterial
): PostPipeline {
  const annotations = createAnnotationLayer();
  const handoff = new PainterHandoff();
  const glow = glowMaterial ? createGlowLayer(glowMaterial) : null;
  let night: NightFrame = { lights: [], night: 0, burning: 1, timeSeconds: 0 };
  let gbuffer = createGBuffer(1, 1);
  let paintRT = createPaintRT(1, 1);
  let currentQuality = quality;
  let width = 1;
  let height = 1;

  const kuwaharaMaterial = createKuwaharaMaterial();
  const finalMaterial = createFinalPassMaterial(style, quality);

  const fullscreenQuad = new Mesh(new PlaneGeometry(2, 2), finalMaterial);
  fullscreenQuad.frustumCulled = false;

  const kuwaharaQuad = new Mesh(new PlaneGeometry(2, 2), kuwaharaMaterial);
  kuwaharaQuad.frustumCulled = false;

  const orthoCamera = new OrthographicCamera(-1, 1, 1, -1, 0, 1);

  const postScene = new Scene();
  postScene.add(fullscreenQuad);

  const kuwaharaScene = new Scene();
  kuwaharaScene.add(kuwaharaQuad);

  function setSize(widthPx: number, heightPx: number): void {
    handoff.clear();
    width = widthPx;
    height = heightPx;

    gbuffer.dispose();
    gbuffer = createGBuffer(width, height);

    paintRT.dispose();
    paintRT = createPaintRT(width, height);

    finalMaterial.uniforms['uResolution']!.value.set(width, height);
    kuwaharaMaterial.uniforms.uResolution.value.set(width, height);
    glow?.uniforms.uResolution.value.set(width, height);
    annotations.resolution.value.set(width, height);

    updateFinalPassQuality(finalMaterial, currentQuality, height);
  }

  function setQuality(q: QualitySettings): void {
    currentQuality = q;
    kuwaharaMaterial.uniforms.uRadius.value = q.kuwaharaRadius;
    updateFinalPassQuality(finalMaterial, q, height);
  }

  function render(scene: Scene, camera: PerspectiveCamera): void {
    renderer.setRenderTarget(gbuffer);
    renderer.clear();
    renderer.render(scene, camera);

    let colorTexture = gbuffer.textures[0]!;

    if (currentQuality.kuwaharaRadius > 0) {
      kuwaharaMaterial.uniforms.tColor.value = gbuffer.textures[0]!;

      renderer.setRenderTarget(paintRT);
      renderer.clear();
      renderer.render(kuwaharaScene, orthoCamera);

      colorTexture = paintRT.texture;
    }

    finalMaterial.uniforms['tColor']!.value = colorTexture;
    finalMaterial.uniforms['tGeom']!.value = gbuffer.textures[1]!;

    const lit = glow !== null && night.night > 0 && night.lights.length > 0;
    /* The painter view moves every surface away from where its flame stands, so there the flames
       show as glows alone. */
    const painterAmount =
      (glowMaterial?.uniforms['uPainterAmount']?.value as number | undefined) ?? 0;
    const eye = camera.position;
    updateFinalPassPools(
      finalMaterial,
      lit ? nearestLights(night.lights, [eye.x, eye.y, eye.z], night.burning, NIGHT.pools) : [],
      camera,
      night.night * (1 - painterAmount),
      night.timeSeconds
    );

    renderer.setRenderTarget(null);
    renderer.render(postScene, orthoCamera);

    if (lit) {
      glow.setLights(night.lights);
      glow.uniforms.tGeom.value = gbuffer.textures[1]!;
      glow.uniforms.uNight.value = night.night;
      glow.uniforms.uBurning.value = night.burning;
      glow.uniforms.uTime.value = night.timeSeconds;
      const autoClear = renderer.autoClear;
      renderer.autoClear = false;
      renderer.render(glow.scene, camera);
      renderer.autoClear = autoClear;
    }
    if (annotations.scene.children.length) {
      annotations.depth.value = gbuffer.textures[1] ?? null;
      const autoClear = renderer.autoClear;
      renderer.autoClear = false;
      renderer.render(annotations.scene, camera);
      renderer.autoClear = autoClear;
    }
    handoff.draw(renderer, orthoCamera, performance.now());
  }

  function dispose(): void {
    gbuffer.dispose();
    paintRT.dispose();
    finalMaterial.dispose();
    kuwaharaMaterial.dispose();
    glow?.dispose();
    fullscreenQuad.geometry.dispose();
    kuwaharaQuad.geometry.dispose();
    handoff.dispose();
  }

  function compile(scene: Scene, camera: PerspectiveCamera): Promise<void> {
    return compileProgramStages(
      renderer,
      compileStages(
        { scene, camera },
        gbuffer,
        { scene: kuwaharaScene, target: paintRT },
        postScene,
        orthoCamera,
        glow?.scene,
        handoff.scene
      )
    );
  }

  return {
    annotations,
    painterHandoff: {
      capture: (scene, camera) => {
        render(scene, camera);
        handoff.capture(renderer);
      },
      get opacity(): number {
        return handoff.opacity;
      },
    },
    setDaylight: (light) => {
      updateFinalPassDaylight(finalMaterial, light);
    },
    setNight: (frame) => {
      night = frame;
    },
    setSize,
    setQuality,
    render,
    compile,
    get passCount(): number {
      return currentQuality.kuwaharaRadius > 0 ? 3 : 2;
    },
    dispose,
  };
}
